import React, { useState, useEffect } from "react";
import { ShoppingBag, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { LOGO_URL } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const links = [
  { label: "Fall Bouquets", href: "#shop-fall" },
  { label: "Christmas 🎄", href: "#shop-christmas" },
  { label: "Stuffers", href: "#shop-stuffers" },
  { label: "Merch", href: "#shop-merch" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-candy-night/85 backdrop-blur-md border-b-4 border-candy-pink"
    >
      <div
        className={`max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between gap-4 transition-all duration-300 ease-in-out ${
          scrolled ? "py-1.5" : "py-2.5"
        }`}
      >
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group min-w-0">
          <img
            src={LOGO_URL}
            alt="The Sweet Tooth Bandit logo"
            className={`rounded-full transition-all duration-300 ease-in-out group-hover:rotate-12 drop-shadow-[2px_2px_0_rgba(0,0,0,0.25)] shrink-0 ${
              scrolled ? "h-9 w-9" : "h-12 w-12 sm:h-14 sm:w-14"
            }`}
          />
          <span
            data-testid="nav-title"
            className={`font-display text-candy-pink tracking-tight leading-none truncate transition-all duration-300 ease-in-out ${
              scrolled ? "text-base sm:text-lg" : "text-xl sm:text-2xl"
            }`}
          >
            THE SWEET TOOTH <span className="text-candy-teal">BANDIT</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-5 lg:gap-8">
          {links.map((l, idx) => (
            <a
              key={idx}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-heading font-600 text-sm uppercase tracking-widest hover:text-candy-pink transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
        <button
          data-testid="open-cart-btn"
          onClick={() => setOpen(true)}
          className={`relative bg-candy-gold text-black font-bold uppercase text-sm tracking-wider border-4 border-black rounded-xl shadow-brutal-sm hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 active:shadow-none transition-all duration-150 flex items-center gap-2 shrink-0 ${
            scrolled ? "py-2 px-3" : "py-2.5 px-4"
          }`}
        >
          <ShoppingBag className="h-5 w-5" />
          <span className="hidden sm:inline">Cart</span>
          {count > 0 && (
            <span
              data-testid="cart-count-badge"
              className="absolute -top-2 -right-2 bg-candy-pink text-white text-xs font-bold h-6 w-6 flex items-center justify-center rounded-full border-2 border-black"
            >
              {count}
            </span>
          )}
        </button>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          className={`md:hidden bg-candy-card text-candy-cream border-4 border-candy-teal rounded-xl shrink-0 active:translate-x-0.5 active:translate-y-0.5 transition-transform duration-150 ${
            scrolled ? "p-2" : "p-2.5"
          }`}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className="md:hidden overflow-hidden border-t-4 border-candy-teal bg-candy-night/95 backdrop-blur-md"
          >
            <div className="px-4 py-3 flex flex-col">
              {links.map((l, idx) => (
                <a
                  key={idx}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setMenuOpen(false);
                    const jump = () => document.querySelector(l.href)?.scrollIntoView({ behavior: "auto", block: "start" });
                    setTimeout(() => {
                      jump();
                      window.history.replaceState(null, "", l.href);
                      setTimeout(jump, 450);
                    }, 320);
                  }}
                  className="font-heading font-700 text-base uppercase tracking-widest text-candy-cream py-3.5 border-b-2 border-candy-teal/20 last:border-b-0 active:text-candy-pink hover:text-candy-pink transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="mobile-menu-cart"
                onClick={() => { setMenuOpen(false); setOpen(true); }}
                className="mt-3 w-full bg-candy-pink text-white font-bold uppercase text-sm tracking-wider py-3 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150 flex items-center justify-center gap-2"
              >
                <ShoppingBag className="h-5 w-5" /> View Cart{count > 0 ? ` (${count})` : ""}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
